/* =====================================================================
   POIESIS ATELIER — shared behaviour (v2)
   ---------------------------------------------------------------------
   Declarative: components opt in with data attributes; no per-section
   scripts. Native scrolling is never altered.

     [data-pa-reveal]       composition revealed once as it enters
                            (time-based, see poiesis-atelier.css)
     [data-pa-drift="1"]    frame whose image drifts slowly with scroll
                            (value = strength; negative reverses)
     [data-pa-sequence]     photograph sequence
       [data-pa-slide] [data-pa-prev] [data-pa-next]
       [data-pa-current] [data-pa-caption]
       data-pa-autoplay="7" seconds between photographs (0 = off);
       [data-pa-toggle] pauses/plays. Autoplay never runs with reduced
       motion, pauses on hover/focus and while off screen.
     [data-pa-detail]       plate with a second, matching photograph;
       [data-pa-detail-toggle] button shows it (keyboard and touch);
       hover shows it on devices that can hover.
     [data-pa-gallery]      browsable set of entries, manual only
       [data-pa-entry]       one entry: photograph and its own caption
                             (name, venue, links) — they change together
       [data-pa-gallery-prev] [data-pa-gallery-next]
       [data-pa-gallery-current]  number of the entry shown
       [data-pa-gallery-status]   polite live region for screen readers
       [data-pa-gallery-controls] shown only when there are 2+ entries
       [data-pa-gallery-stage]    horizontal swipe area (touch, pen)
       Left/Right arrow keys browse while focus is inside the gallery.

   Theme editor: re-initialises on shopify:section:load.
   ===================================================================== */
(() => {
  if (window.PoiesisAtelier) return;

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hasIO = 'IntersectionObserver' in window;
  const vh = () => window.innerHeight || root.clientHeight;

  function setMotionFlag() {
    root.classList.toggle('pa-motion', hasIO && !reduced.matches);
  }

  /* ---------- Reveal: once, as the composition approaches ----------
     The trigger is the composition's own content, not its padding: we
     watch its parts (title, photograph, copy) and start the whole
     composition together as soon as the first part edges into view, so
     the movement is seen and visitors never wait on empty space. */
  let revealObserver = null;
  const ownerOf = new WeakMap();
  function reveal(owner) {
    if (owner.classList.contains('is-in')) return;
    owner.classList.add('is-in');
    (owner.__paWatched || []).forEach((t) => revealObserver && revealObserver.unobserve(t));
  }
  function initReveal(scope) {
    const items = scope.querySelectorAll('[data-pa-reveal]:not([data-pa-ready])');
    if (!items.length) return;
    if (!hasIO) { items.forEach((el) => el.classList.add('is-in')); return; }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const owner = ownerOf.get(entry.target);
            if (owner) reveal(owner);
          }
        });
      }, { rootMargin: '0px 0px -4% 0px', threshold: 0 });
    }
    items.forEach((el) => {
      el.dataset.paReady = 'true';
      /* Anything already above the viewport (e.g. after a reload part-way
         down the page) is shown at once rather than animated. */
      if (el.getBoundingClientRect().bottom < 0) { el.classList.add('is-in'); return; }
      const parts = Array.from(el.querySelectorAll('[data-pa-part], .pa-mask'))
        .filter((p) => p.closest('[data-pa-reveal]') === el);
      const targets = parts.length ? parts : [el];
      el.__paWatched = targets;
      targets.forEach((t) => { ownerOf.set(t, el); revealObserver.observe(t); });
    });
  }

  /* ---------- Drift ---------- */
  const drifters = new Set();
  let frame = null;
  function render() {
    frame = null;
    const h = vh();
    drifters.forEach((el) => {
      if (!el.isConnected) { drifters.delete(el); return; }
      if (reduced.matches) { el.style.removeProperty('--pa-drift-y'); return; }
      const r = el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > h + 200) return;
      const progress = Math.max(-1, Math.min(1, ((r.top + r.height / 2) - h / 2) / (h / 2 + r.height / 2)));
      const strength = parseFloat(el.dataset.paDrift) || 1;
      el.style.setProperty('--pa-drift-y', (progress * r.height * 0.05 * strength).toFixed(1) + 'px');
    });
  }
  function schedule() { if (frame === null) frame = requestAnimationFrame(render); }
  function initDrift(scope) {
    scope.querySelectorAll('[data-pa-drift]').forEach((el) => drifters.add(el));
    schedule();
  }

  /* ---------- Sequence ---------- */
  function initSequence(scope) {
    scope.querySelectorAll('[data-pa-sequence]').forEach((el) => {
      if (el.dataset.paSequenceReady) return;
      el.dataset.paSequenceReady = 'true';
      const slides = Array.from(el.querySelectorAll('[data-pa-slide]'));
      if (slides.length < 2) return;
      const caption = el.querySelector('[data-pa-caption]');
      const current = el.querySelector('[data-pa-current]');
      const toggle = el.querySelector('[data-pa-toggle]');
      const seconds = parseFloat(el.dataset.paAutoplay) || 0;
      let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
      let timer = null;
      let userPaused = false;
      let hovering = false;
      let visible = false;

      function show(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        if (current) current.textContent = index + 1;
        if (caption) caption.textContent = slides[index].dataset.caption || '';
      }
      function canPlay() { return seconds > 0 && !reduced.matches && !userPaused && !hovering && visible; }
      function sync() {
        clearInterval(timer);
        timer = null;
        if (canPlay()) timer = setInterval(() => show(index + 1), seconds * 1000);
        if (toggle) {
          const playing = seconds > 0 && !reduced.matches && !userPaused;
          toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
          toggle.setAttribute('aria-label', playing ? 'Pause photographs' : 'Play photographs');
          const label = toggle.querySelector('[data-pa-toggle-label]');
          if (label) label.textContent = playing ? 'Pause' : 'Play';
        }
      }
      const prev = el.querySelector('[data-pa-prev]');
      const next = el.querySelector('[data-pa-next]');
      if (prev) prev.addEventListener('click', () => { show(index - 1); sync(); });
      if (next) next.addEventListener('click', () => { show(index + 1); sync(); });
      if (toggle) toggle.addEventListener('click', () => { userPaused = !userPaused; sync(); });
      el.addEventListener('mouseenter', () => { hovering = true; sync(); });
      el.addEventListener('mouseleave', () => { hovering = false; sync(); });
      el.addEventListener('focusin', () => { hovering = true; sync(); });
      el.addEventListener('focusout', (e) => { if (!el.contains(e.relatedTarget)) { hovering = false; sync(); } });
      if (hasIO) {
        new IntersectionObserver((entries) => {
          visible = entries[0].isIntersecting;
          sync();
        }, { threshold: 0.25 }).observe(el);
      } else { visible = true; }
      const onPref = () => sync();
      if (reduced.addEventListener) reduced.addEventListener('change', onPref); else reduced.addListener(onPref);
      sync();
    });
  }

  /* ---------- Detail photograph ---------- */
  function initDetail(scope) {
    scope.querySelectorAll('[data-pa-detail]').forEach((el) => {
      if (el.dataset.paDetailReady) return;
      el.dataset.paDetailReady = 'true';
      const button = el.querySelector('[data-pa-detail-toggle]');
      let pinned = false;
      function set(on) {
        el.classList.toggle('is-detail', on);
        if (button) {
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          const label = button.querySelector('[data-pa-detail-label]');
          if (label) label.textContent = on ? (button.dataset.labelOff || 'Whole work') : (button.dataset.labelOn || 'Detail');
        }
      }
      if (button) button.addEventListener('click', () => { pinned = !pinned; set(pinned); });
      el.addEventListener('mouseenter', () => { if (canHover.matches && !pinned) set(true); });
      el.addEventListener('mouseleave', () => { if (canHover.matches && !pinned) set(false); });
    });
  }

  /* ---------- Gallery: browsed by the visitor, never on a timer ----------
     Each entry keeps its photograph and caption together, so a portrait,
     its name and its profile link (or a photograph and its venue) can
     never fall out of step. Inactive entries are inert. */
  function initGallery(scope) {
    scope.querySelectorAll('[data-pa-gallery]').forEach((el) => {
      if (el.dataset.paGalleryReady) return;
      el.dataset.paGalleryReady = 'true';
      const entries = Array.from(el.querySelectorAll('[data-pa-entry]'))
        .filter((e) => e.closest('[data-pa-gallery]') === el);
      const controls = el.querySelector('[data-pa-gallery-controls]');
      if (entries.length < 2) {
        el.classList.add('is-single');
        if (controls) controls.hidden = true;
        return;
      }
      el.classList.add('is-browsable');
      if (controls) controls.hidden = false;
      const current = el.querySelector('[data-pa-gallery-current]');
      const status = el.querySelector('[data-pa-gallery-status]');
      let index = Math.max(0, entries.findIndex((e) => e.classList.contains('is-active')));

      function warm(i) {
        const entry = entries[(i + entries.length) % entries.length];
        entry.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      }
      function show(next, announce) {
        index = (next + entries.length) % entries.length;
        entries.forEach((entry, i) => {
          const on = i === index;
          entry.classList.toggle('is-active', on);
          entry.setAttribute('aria-hidden', on ? 'false' : 'true');
          if ('inert' in entry) entry.inert = !on;
        });
        warm(index); warm(index + 1); warm(index - 1);
        if (current) current.textContent = String(index + 1);
        if (announce && status) {
          const label = entries[index].dataset.paLabel || '';
          status.textContent = (index + 1) + ' of ' + entries.length + (label ? ': ' + label : '');
        }
      }

      const prev = el.querySelector('[data-pa-gallery-prev]');
      const next = el.querySelector('[data-pa-gallery-next]');
      if (prev) prev.addEventListener('click', () => show(index - 1, true));
      if (next) next.addEventListener('click', () => show(index + 1, true));

      el.addEventListener('keydown', (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        if (e.key === 'ArrowLeft') { show(index - 1, true); e.preventDefault(); }
        else if (e.key === 'ArrowRight') { show(index + 1, true); e.preventDefault(); }
      });

      /* Swipe: only a clearly horizontal gesture browses; vertical
         scrolling stays with the browser (touch-action: pan-y). */
      const stage = el.querySelector('[data-pa-gallery-stage]') || el;
      let sx = null; let sy = null; let pid = null;
      stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        sx = e.clientX; sy = e.clientY; pid = e.pointerId;
      }, { passive: true });
      stage.addEventListener('pointerup', (e) => {
        if (sx === null || e.pointerId !== pid) return;
        const dx = e.clientX - sx; const dy = e.clientY - sy;
        sx = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) show(index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      stage.addEventListener('pointercancel', () => { sx = null; }, { passive: true });

      show(index, false);
    });
  }

  function init(scope) {
    scope = scope || document;
    setMotionFlag();
    initReveal(scope);
    initDrift(scope);
    initSequence(scope);
    initDetail(scope);
    initGallery(scope);
  }

  /* Scroll events from the page's own scroll container reach us in the
     capture phase; nothing here changes how the page scrolls. */
  window.addEventListener('scroll', schedule, { passive: true });
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  window.addEventListener('resize', schedule, { passive: true });
  const onReduced = () => { setMotionFlag(); schedule(); };
  if (reduced.addEventListener) reduced.addEventListener('change', onReduced); else reduced.addListener(onReduced);
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.PoiesisAtelier = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
